/**
 * The place list (§B list, §B.1, §B.2, §B.3, §E.9, §H.2).
 *
 * One listbox tab stop with aria-activedescendant. ↑/↓, Home/End and
 * PageUp/PageDown move the active option; after 120 ms the map previews it (as
 * hover). Enter selects (focus stays), Esc deselects. Pointer hover previews
 * after a 120 ms dwell and clears at once on leave; never on touch or kiosk.
 *
 * Choreography: when the group, category, sort or search mode changes, the rows
 * leave (168 leave) and the new ones arrive with .ld-in (fy 8, 420 settle,
 * 40 ms stagger for the first ten, the rest together). Typing in the search
 * field swaps rows quietly. The selected row is kept in view (the browser's smooth
 * scroll, below a stuck header). Under "All" the rows sit in category sections
 * with sticky headers.
 *
 * Hover light from the map (store hoveredId) and the selected bar's shrink are
 * class toggles on the DOM, so a pointer moving over the map never re-renders
 * the list.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent,
  type JSX,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { Button, CSS_ESC, DUR } from "@/design";
import type { Category, GroupId, Place } from "@/contracts/data";
import type { SortKey } from "@/contracts/store";
import { GROUPS } from "@/contracts/taxonomy";
import { actions, appStore, getApp, useApp } from "@/store/store";
import { COPY, emptyTitle, listIndex, optionLabel, rowMeta, rowTitle, scrollTarget, sectionize, staggerIndex, type Section } from "./format";
import { optionId, PlaceRow } from "./PlaceRow";

const DWELL_MS = 120;
const LEAVE_MS = 168;
/** A list remounted by a layout switch (side panel ↔ sheet) picks up the active option it had, if it is back this soon. */
const REMOUNT_MS = 3000;
/** The active option of the listbox that last unmounted (by listbox id), for the remount after a layout switch. */
const lastActive = new Map<string, { pid: string; t: number }>();

type CSSVars = CSSProperties & { [k: `--${string}`]: string | number | undefined };

const GROUP_SHORT = new Map<GroupId, string>(GROUPS.map((g) => [g.id, g.short]));

export interface PlaceListProps {
  id: string;
  places: Place[];
  /** Changes when the rows should leave and arrive again (group, category, sort, search mode). */
  listKey: string;
  grouped: boolean;
  searching: boolean;
  query: string;
  sort: SortKey;
  selectedId: string | null;
  categories: ReadonlyMap<string, Category>;
  roadRefs: ReadonlyMap<string, string>;
  label: string;
  /** Pointer hover previews on the map (off on touch and kiosk). */
  hoverPreview: boolean;
  reduced: boolean;
  loading: boolean;
  /** Bump to replay the row arrival (the panel's own arrival after the intro). */
  arrival: number;
  onClearSearch: () => void;
  /** Rendered at the end of the scrolling list (mobile: About, attribution, Powered by). */
  endnote?: ReactNode;
  scrollRef: RefObject<HTMLDivElement | null>;
}

interface View {
  key: string;
  places: Place[];
  grouped: boolean;
  gen: number;
  leaving: boolean;
  /** Rows mounted in this view stagger in (key change, arrival); a quiet swap does not. */
  stagger: boolean;
}

export function PlaceList(props: PlaceListProps): JSX.Element {
  const { id, places, listKey, grouped, searching, query, sort, selectedId, categories, roadRefs, label, hoverPreview, reduced, loading, arrival, onClearSearch, endnote, scrollRef } = props;
  const boxRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);

  /* ── view: the rows on screen, lagging the live list through the leave ── */
  const [view, setView] = useState<View>(() => ({ key: listKey, places, grouped, gen: 0, leaving: false, stagger: true }));
  const live = useRef({ places, grouped, listKey });
  live.current = { places, grouped, listKey };
  /** The list key whose scroll went back to the top while the old rows were out of sight. */
  const resetFor = useRef<string | null>(null);

  useEffect(() => {
    if (view.key === listKey) {
      if (view.places !== places || view.grouped !== grouped) setView((v) => ({ ...v, places, grouped, stagger: false }));
      return undefined;
    }
    if (reduced || !view.places.length) {
      setView((v) => ({ key: listKey, places, grouped, gen: v.gen + 1, leaving: false, stagger: true }));
      return undefined;
    }
    if (!view.leaving) setView((v) => ({ ...v, leaving: true }));
    const t = window.setTimeout(() => {
      const l = live.current;
      // Back to the top now, while the faded rows are all there is to lay out: the same write after the new
      // rows mount would force their first layout inside React's commit (QA L2-07).
      const sc = scrollRef.current;
      if (sc?.hasAttribute("data-scrolled")) sc.scrollTop = 0;
      resetFor.current = l.listKey;
      setView((v) => ({ key: l.listKey, places: l.places, grouped: l.grouped, gen: v.gen + 1, leaving: false, stagger: true }));
    }, LEAVE_MS);
    return () => window.clearTimeout(t);
  }, [listKey, places, grouped, reduced, view.key, view.places, view.grouped, view.leaving, scrollRef]);

  // The panel arrived after the intro: the rows arrive with it.
  const lastArrival = useRef(arrival);
  useEffect(() => {
    if (arrival === lastArrival.current) return;
    lastArrival.current = arrival;
    setView((v) => ({ ...v, gen: v.gen + 1, stagger: true }));
  }, [arrival]);

  const sections: Section[] = sectionize(view.places, [...categories.values()], view.grouped);
  const ids = view.places.map((p) => p.id);

  /* ── active option (keyboard) ─────────────────────────────────────── */
  const [activeRaw, setActive] = useState<string | null>(null);
  const activeId = activeRaw && ids.includes(activeRaw) ? activeRaw : null;
  const activeNow = useRef(activeId);
  activeNow.current = activeId;
  useEffect(
    () => () => {
      if (activeNow.current) lastActive.set(id, { pid: activeNow.current, t: performance.now() });
    },
    [id],
  );

  /* ── scrolling ────────────────────────────────────────────────────── */
  const rowEl = useCallback((pid: string) => boxRef.current?.querySelector<HTMLElement>(`[data-id="${CSS_ESC(pid)}"]`) ?? null, []);

  const reveal = useCallback(
    (pid: string, animate: boolean) => {
      const sc = scrollRef.current;
      const row = rowEl(pid);
      if (!sc || !row) return;
      const head = boxRef.current?.querySelector<HTMLElement>(".ld-loc-panel-sechead");
      const padTop = (head?.offsetHeight ?? 0) + 4;
      // offsetTop is layout-only (unaffected by the arrival transform); the scroller is the rows'
      // offsetParent (position: relative), so this is the row's top within the scrolled content.
      let top = row.offsetTop;
      for (let p = row.offsetParent as HTMLElement | null; p && p !== sc && sc.contains(p); p = p.offsetParent as HTMLElement | null) top += p.offsetTop;
      const to = scrollTarget(top, row.offsetHeight, sc.scrollTop, sc.clientHeight, padTop, 12, sc.scrollHeight - sc.clientHeight);
      if (to == null) return;
      if (!animate) {
        sc.scrollTop = to;
        return;
      }
      // The browser's own smooth scroll: it moves the offset in its frame step, so no script writes
      // scrollTop (and forces a layout) every frame while the route draws (QA L2-07). A wheel or a touch
      // takes over from it, as it did from the glide.
      sc.scrollTo({ top: to, behavior: "smooth" });
    },
    [rowEl, scrollRef],
  );

  // A new list (group, chip, sort, search) starts at its top; the reveal below then brings a selection
  // into view. (I1: without this a swap kept the old offset and hid the nearest rows.)
  const lastKey = useRef(view.key);
  useLayoutEffect(() => {
    if (view.key === lastKey.current) return;
    lastKey.current = view.key;
    if (resetFor.current === view.key) return; // done while the old rows were out of sight
    // only when it has scrolled (the sentinel says so): a scrollTop write lays out the rows that just mounted
    const sc = scrollRef.current;
    if (sc?.hasAttribute("data-scrolled")) sc.scrollTop = 0;
  }, [view.key, scrollRef]);

  // Keep the selected row in view (also after the rows re-arrive). Measured once the frame that carries the
  // selection has rendered: layout is clean then, so the reads force nothing in the select task (QA L2-07).
  useEffect(() => {
    if (!selectedId || view.leaving) return undefined;
    let post: ReturnType<typeof setTimeout> | undefined;
    const raf = requestAnimationFrame(() => {
      post = setTimeout(() => reveal(selectedId, !reduced), 0);
    });
    return () => {
      cancelAnimationFrame(raf);
      if (post) clearTimeout(post);
    };
  }, [selectedId, view.gen, view.leaving, reduced, reveal]);

  // The top fade appears once the list has scrolled: a 1 px sentinel at the top leaves the list's view.
  // No scroll handler reads scrollTop, so scrolling never forces a layout (QA L2-07).
  useEffect(() => {
    const sc = scrollRef.current;
    if (!sc) return undefined;
    const top = topRef.current;
    let io: IntersectionObserver | null = null;
    let onScroll: (() => void) | null = null;
    if (top && typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver((entries) => sc.toggleAttribute("data-scrolled", !entries[entries.length - 1].isIntersecting), { root: sc });
      io.observe(top);
    } else {
      const h = () => sc.toggleAttribute("data-scrolled", sc.scrollTop > 0);
      onScroll = h;
      h();
      sc.addEventListener("scroll", h, { passive: true });
    }
    return () => {
      io?.disconnect();
      if (onScroll) sc.removeEventListener("scroll", onScroll);
    };
  }, [scrollRef]);

  // Kiosk attract: the list goes back to the top.
  const attract = useApp((s) => s.phase === "attract");
  useEffect(() => {
    if (attract && scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [attract, scrollRef]);

  /* ── hover previews (120 ms dwell) ────────────────────────────────── */
  const dwell = useRef<{ timer: number; id: string | null; set: boolean }>({ timer: 0, id: null, set: false });
  const unpreview = useCallback(() => {
    const d = dwell.current;
    window.clearTimeout(d.timer);
    if (d.set && getApp().hoveredId === d.id) actions.hover(null);
    d.id = null;
    d.set = false;
  }, []);
  const preview = useCallback(
    (pid: string) => {
      const d = dwell.current;
      if (d.id === pid) return;
      unpreview();
      d.id = pid;
      d.timer = window.setTimeout(() => {
        d.set = true;
        actions.hover(pid);
      }, DWELL_MS);
    },
    [unpreview],
  );
  useEffect(() => () => unpreview(), [unpreview]);

  const onPointerOver = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!hoverPreview || e.pointerType !== "mouse") return;
    const row = (e.target as Element).closest<HTMLElement>("[data-id]");
    if (row?.dataset.id) preview(row.dataset.id);
    else unpreview();
  };
  const onPointerLeave = () => {
    if (dwell.current.id) unpreview();
  };

  // Touch and kiosk: the rake light follows the touch point for 420 ms after pointerdown.
  // Any press also marks the coming focus as a pointer focus (no keyboard start-up scroll).
  const pressing = useRef(false);
  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    pressing.current = true;
    window.setTimeout(() => {
      pressing.current = false;
    }, 0);
    if (e.pointerType === "mouse") return;
    const row = (e.target as Element).closest<HTMLElement>("[data-id]");
    if (!row) return;
    row.classList.add("is-rake");
    window.setTimeout(() => row.classList.remove("is-rake"), DUR.calm);
  };

  /* ── hover light from the store (pins on the map light their row) ─── */
  const applyHover = useCallback((hid: string | null) => {
    const box = boxRef.current;
    if (!box) return;
    box.querySelectorAll<HTMLElement>(".ld-loc-panel-row.is-hover").forEach((r) => {
      if (r.dataset.id !== hid) r.classList.remove("is-hover");
    });
    if (hid) rowEl(hid)?.classList.add("is-hover");
  }, [rowEl]);
  useEffect(() => appStore.subscribe((s, prev) => {
    if (s.hoveredId !== prev.hoveredId) applyHover(s.hoveredId);
  }), [applyHover]);
  // Re-apply after every render (a row whose className React rewrote loses the class).
  useLayoutEffect(() => applyHover(getApp().hoveredId));

  /* ── the selected bar shrinks (168 leave) when a row lets go ──────── */
  const lastSelected = useRef(selectedId);
  useLayoutEffect(() => {
    const prev = lastSelected.current;
    lastSelected.current = selectedId;
    if (!prev || prev === selectedId || reduced) return undefined;
    const row = rowEl(prev);
    if (!row) return undefined;
    row.classList.add("is-unselecting");
    const t = window.setTimeout(() => row.classList.remove("is-unselecting"), LEAVE_MS);
    return () => {
      window.clearTimeout(t);
      row.classList.remove("is-unselecting");
    };
  }, [selectedId, reduced, rowEl]);

  /* ── keyboard ─────────────────────────────────────────────────────── */
  const pageSize = () => {
    const sc = scrollRef.current;
    const row = boxRef.current?.querySelector<HTMLElement>(".ld-loc-panel-row");
    return sc && row ? Math.max(1, Math.floor(sc.clientHeight / (row.offsetHeight + 2)) - 1) : 5;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const cur = activeId ? ids.indexOf(activeId) : selectedId ? ids.indexOf(selectedId) : -1;
    switch (e.key) {
      case "ArrowDown":
      case "ArrowUp":
      case "Home":
      case "End":
      case "PageDown":
      case "PageUp": {
        e.preventDefault();
        if (view.leaving) return; // the outgoing rows are not navigable
        const next = listIndex(cur, e.key, ids.length, pageSize());
        if (next < 0) return;
        const pid = ids[next];
        setActive(pid);
        reveal(pid, false);
        preview(pid);
        return;
      }
      case "Enter":
      case " ": {
        if (cur < 0 || view.leaving) return;
        e.preventDefault();
        setActive(ids[cur]);
        actions.select(ids[cur], "list");
        return;
      }
      case "Escape": {
        if (!getApp().selectedId) return;
        e.preventDefault();
        e.stopPropagation();
        actions.select(null);
        return;
      }
      default:
    }
  };

  // New rows arrived while the list has focus: keep the active option in view.
  useLayoutEffect(() => {
    if (activeId && boxRef.current && document.activeElement === boxRef.current) reveal(activeId, false);
  }, [view.gen]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFocus = (e: FocusEvent<HTMLDivElement>) => {
    if (pressing.current || e.target !== e.currentTarget || activeId || !ids.length) return;
    // reflow (WCAG 1.4.10): a list remounted by a layout switch keeps the option the user was on
    const kept = lastActive.get(id);
    lastActive.delete(id);
    const resumed = kept && performance.now() - kept.t < REMOUNT_MS && ids.includes(kept.pid) ? kept.pid : null;
    const start = resumed ?? (selectedId && ids.includes(selectedId) ? selectedId : ids[0]);
    setActive(start);
    reveal(start, false);
  };
  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) unpreview();
  };

  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    const row = (e.target as Element).closest<HTMLElement>("[data-id]");
    const pid = row?.dataset.id;
    if (!pid) return;
    setActive(pid);
    actions.select(pid, "list");
  };

  /* ── rows ─────────────────────────────────────────────────────────── */
  const renderRow = (p: Place, i: number) => {
    const cat = categories.get(p.category);
    const kind = cat?.kind;
    const meta = rowMeta(p, {
      kind,
      categoryLabel: cat?.label,
      searching,
      groupShort: GROUP_SHORT.get(p.group),
      roadRef: kind === "road" && p.geometryRef ? (roadRefs.get(p.geometryRef) ?? null) : null,
      // the status sits on the meta line unless the row has no figures (future corridors)
      status: p.status !== "existing" && kind !== "future",
    });
    return (
      <PlaceRow
        key={`${view.gen}:${p.id}`}
        place={p}
        icon={p.icon ?? cat?.icon ?? "mapPin"}
        kind={kind}
        sort={sort}
        meta={meta}
        label={optionLabel(p, { kind, meta, note: true })}
        title={rowTitle(p, kind)}
        selected={p.id === selectedId}
        active={p.id === activeId}
        stagger={view.stagger ? staggerIndex(i) : 0}
      />
    );
  };

  const empty = !loading && searching && !view.places.length && !view.leaving;

  return (
    <div className="ld-loc-panel-listwrap">
      <div ref={scrollRef} className="ld-loc-panel-list" data-grouped={view.grouped || undefined}>
        <div ref={topRef} className="ld-loc-panel-top" aria-hidden="true" />
        {loading ? (
          <div role="listbox" id={id} aria-label={COPY.aria} aria-busy="true" className="ld-loc-panel-options">
            {Array.from({ length: 7 }, (_, i) => (
              <div key={i} className="ld-loc-panel-skel" aria-hidden="true" style={{ animationDelay: `${i * 120}ms` }}>
                <i />
                <span>
                  <i />
                  <i />
                </span>
                <i />
              </div>
            ))}
          </div>
        ) : (
          <div
            ref={boxRef}
            role="listbox"
            id={id}
            tabIndex={0}
            aria-label={label}
            aria-activedescendant={activeId ? optionId(activeId) : undefined}
            className={view.leaving ? "ld-loc-panel-options is-leaving" : "ld-loc-panel-options"}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            onBlur={onBlur}
            onClick={onClick}
            onPointerOver={onPointerOver}
            onPointerLeave={onPointerLeave}
            onPointerDown={onPointerDown}
          >
            {sections.map((s) =>
              s.category ? (
                <div key={`${view.gen}:${s.category.id}`} role="group" aria-label={s.category.label} className="ld-loc-panel-section">
                  <div className="ld-loc-panel-sechead ld-in" aria-hidden="true" style={{ "--i": view.stagger ? staggerIndex(s.start) : 0 } as CSSVars}>
                    <span className="ld-meta">
                      {s.category.label} · {s.places.length}
                    </span>
                  </div>
                  {s.places.map((p, j) => renderRow(p, s.start + j))}
                </div>
              ) : (
                s.places.map((p, j) => renderRow(p, s.start + j))
              ),
            )}
          </div>
        )}
        {empty && (
          <div className="ld-loc-panel-empty ld-fade">
            <p className="ld-sub ld-loc-panel-emptytitle">{emptyTitle(query)}</p>
            <p className="ld-loc-panel-emptydetail">{COPY.emptyDetail}</p>
            <Button variant="glass" size="sm" onClick={onClearSearch}>
              {COPY.searchClear}
            </Button>
          </div>
        )}
        {endnote}
      </div>
    </div>
  );
}
